import { Link } from 'react-router-dom';
import { Play } from 'lucide-react';
import { StatCard } from '@/components/ui/stat-card';
import { ScoreBadge } from '@/components/ui/score-badge';
import { TeamFitBadge } from '@/components/ui/team-fit-badge';
import { Button } from '@/components/ui/button';
import { useMatches, useRunMatching } from '@/hooks/use-matches';
import { useTeamProfile } from '@/hooks/use-team';
import { useDataStats } from '@/hooks/use-admin';

export function DashboardPage() {
  const { data: matchesData } = useMatches(1, 5);
  const { data: statsData } = useDataStats();
  const { data: teamData } = useTeamProfile();
  const runMatching = useRunMatching();

  const matches = matchesData?.data ?? [];
  const assignmentCount = statsData?.data?.assignments ?? 0;
  const team = teamData?.data;

  const hasError = matchesData?.error || teamData?.error;

  const highScoreMatches = matches.filter((m) => m.aiScore && parseFloat(m.aiScore) >= 7);

  const coverageValues = Object.values(team?.coverageMap ?? {});
  const avgCoverage =
    coverageValues.length > 0
      ? Math.round(coverageValues.reduce((a, b) => a + b, 0) / coverageValues.length)
      : 0;

  const gapCount = team?.identifiedGaps?.length ?? 0;

  return (
    <div className="space-y-6">
      {hasError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400">
          Some data failed to load. Try refreshing the page.
        </div>
      )}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => runMatching.mutate({})} disabled={runMatching.isPending}>
            <Play className="mr-1.5 h-3.5 w-3.5" />
            {runMatching.isPending ? 'Running...' : 'Run Matching'}
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          label="Active Assignments"
          value={assignmentCount}
          subtitle={`${assignmentCount} total`}
          subtitleColor="muted"
        />
        <StatCard
          label="New Matches"
          value={highScoreMatches.length}
          subtitle="Score >= 7.0"
          subtitleColor="blue"
        />
        <StatCard
          label="Team Coverage"
          value={`${avgCoverage}%`}
          subtitle={gapCount > 0 ? `${gapCount} skill gaps` : 'No gaps identified'}
          subtitleColor={gapCount > 0 ? 'yellow' : 'green'}
        />
      </div>

      {/* Top Matches */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Top Matches</h2>
          <Link to="/matches" className="text-xs text-primary hover:underline">
            View all matches &rarr;
          </Link>
        </div>

        {matches.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No matches yet. Click "Run Matching" to analyze your team against active assignments.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-2.5 text-left text-xs font-medium uppercase text-muted-foreground">
                    Candidate
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium uppercase text-muted-foreground">
                    Assignment
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium uppercase text-muted-foreground">
                    AI Score
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium uppercase text-muted-foreground">
                    Team Fit
                  </th>
                </tr>
              </thead>
              <tbody>
                {matches.map((match) => (
                  <tr key={match.id} className="border-b last:border-0">
                    <td className="px-4 py-2.5 font-medium">{match.profileName}</td>
                    <td className="px-4 py-2.5">{match.assignmentTitle}</td>
                    <td className="px-4 py-2.5">
                      <ScoreBadge score={match.aiScore} />
                    </td>
                    <td className="px-4 py-2.5">
                      <TeamFitBadge score={match.teamFitScore} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
